import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import { Plus, X, UserCheck, UserX, Pencil, Trash2, Eye, Users as UsersIcon, Clock, Shield, RotateCcw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const ROLES = ["EMPLOYEE","MANAGER","DEPARTMENT_HEAD","AUDIT","HR","ADMIN"];
const DEPARTMENTS = ["Technical Support","Development","Customer Support","Billing","Other"];

const ALL_TABS = [
  { key:"dashboard",      label:"Dashboard" },
  { key:"projects",       label:"Projects" },
  { key:"tasks",          label:"Tasks" },
  { key:"tickets",        label:"Tickets" },
  { key:"timesheets",     label:"Timesheets" },
  { key:"crm",            label:"CRM" },
  { key:"communications", label:"Communications" },
  { key:"quality",        label:"Quality" },
  { key:"reports",        label:"Reports" },
  { key:"approvals",      label:"Approvals" },
  { key:"users",          label:"Users" },
  { key:"roles-permissions", label:"Roles & Permissions" },
  { key:"hr",             label:"HR" },
  { key:"my-requests",    label:"My Requests" },
  { key:"request-approvals", label:"Request Approvals" },
  { key:"requests-report",label:"Requests Report" },
];

const roleColors: Record<string,string> = {
  ADMIN:           "bg-red-100 text-red-700",
  DEPARTMENT_HEAD: "bg-purple-100 text-purple-700",
  MANAGER:         "bg-blue-100 text-blue-700",
  AUDIT:           "bg-teal-100 text-teal-700",
  HR:              "bg-indigo-100 text-indigo-700",
  EMPLOYEE:        "bg-gray-100 text-gray-700",
};

const blankForm = { name:"", email:"", password:"", role:"EMPLOYEE", department:"", managerId:"", departmentHeadId:"", isActive:true };

const Users: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers]           = useState<any[]>([]);
  const [requests, setRequests]     = useState<any[]>([]);
  const [showForm, setShowForm]     = useState(false);
  const [editUser, setEditUser]     = useState<any>(null);
  const [form, setForm]             = useState<any>(blankForm);
  const [tabPerms, setTabPerms]     = useState<string[]>([]);
  const [activeTab, setActiveTab]   = useState<"users"|"requests">("users");
  const [search, setSearch]         = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [hideDeleted, setHideDeleted] = useState(false);

  const load = () => {
    api.get("/users?includeDeleted=true").then(r => setUsers(r.data));
    api.get("/user-requests").then(r => setRequests(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditUser(null);
    setForm(blankForm);
    setTabPerms(ALL_TABS.map(t => t.key));
    setShowForm(true);
  };

  const openEdit = (u: any) => {
    setEditUser(u);
    setForm({ name:u.name, email:u.email, password:"", role:u.role, department:u.department||"", managerId:u.managerId||"", departmentHeadId:u.departmentHeadId||"", isActive:u.isActive });
    api.get(`/users/${u.id}/tab-permissions`).then(r => {
      setTabPerms(r.data?.length ? r.data : ALL_TABS.map(t => t.key));
    }).catch(() => setTabPerms(ALL_TABS.map(t => t.key)));
    setShowForm(true);
  };

  const toggleTab = (key: string) => {
    setTabPerms(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}`, form);
        await api.put(`/users/${editUser.id}/tab-permissions`, { tabs: tabPerms });
        toast.success("User updated!");
      } else {
        const res = await api.post("/users", form);
        await api.put(`/users/${res.data.id}/tab-permissions`, { tabs: tabPerms });
        toast.success("User created!");
      }
      setShowForm(false); load();
    } catch (err: any) { toast.error(err?.response?.data?.message || "Failed"); }
  };

  const toggleActive = async (id: string, name: string) => {
    if (!window.confirm(`Toggle account status for ${name}?`)) return;
    try { await api.post(`/users/${id}/toggle-active`); toast.success("Status updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const deleteUser = async (id: string, name: string) => {
    if (!window.confirm(`Delete user ${name}? The user will be deactivated and hidden from active lists. All data will be preserved.`)) return;
    try { await api.delete(`/users/${id}`); toast.success("User deleted (soft delete)"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const restoreUser = async (id: string, name: string) => {
    if (!window.confirm(`Restore user ${name}? They will be reactivated and able to log in again.`)) return;
    try { await api.post(`/users/${id}/restore`); toast.success("User restored successfully"); load(); }
    catch { toast.error("Failed to restore"); }
  };

  const approveRequest = async (id: string) => {
    try { await api.post(`/user-requests/${id}/approve`); toast.success("User approved & created!"); load(); }
    catch { toast.error("Failed to approve"); }
  };
  const rejectRequest = async (id: string) => {
    if (!window.confirm("Reject this request?")) return;
    try { await api.post(`/user-requests/${id}/reject`); toast.success("Request rejected"); load(); }
    catch { toast.error("Failed"); }
  };

  const managers = users.filter(u => (u.role === "MANAGER" || u.role === "DEPARTMENT_HEAD" || u.role === "ADMIN") && !u.isDeleted);
  const deptHeads = users.filter(u => (u.role === "DEPARTMENT_HEAD" || u.role === "ADMIN") && !u.isDeleted);
  const pendingCount = requests.filter(r => r.status === "PENDING").length;

  const filtered = users.filter(u => {
    if (hideDeleted && u.isDeleted) return false;
    const matchSearch = u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = !roleFilter || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UsersIcon size={24} className="text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        </div>
        {isAdmin && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm">
            <Plus size={16} /> New User
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit">
        <button onClick={() => setActiveTab("users")} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab==="users" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600"}`}>
          All Users ({users.filter(u => !u.isDeleted).length})
        </button>
        <button onClick={() => setActiveTab("requests")} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${activeTab==="requests" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600"}`}>
          Account Requests
          {pendingCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{pendingCount}</span>}
        </button>
      </div>

      {activeTab === "users" && (
        <>
          <div className="flex gap-3 items-center">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="">All Roles</option>
              {ROLES.map(r => <option key={r}>{r.replace("_"," ")}</option>)}
            </select>
            {isAdmin && (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 whitespace-nowrap">
                <input type="checkbox" checked={hideDeleted} onChange={e => setHideDeleted(e.target.checked)} className="w-4 h-4 text-red-600 rounded" />
                Hide Deleted Users
              </label>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["User","Role","Department","Manager / Dept Head","Hours","Status","Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(u => (
                  <tr key={u.id} className={`hover:bg-gray-50 ${u.isDeleted ? "bg-red-50/50" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${u.isDeleted ? "bg-gray-400" : "bg-blue-500"}`}>
                          {u.name[0].toUpperCase()}
                        </div>
                        <div>
                          <button onClick={() => navigate(`/users/${u.id}`)} className={`font-medium hover:underline text-left ${u.isDeleted ? "text-gray-400" : "text-blue-600"}`}>
                            {u.name}
                            {u.isDeleted && <span className="ml-1.5 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">(Inactive)</span>}
                          </button>
                          <p className="text-xs text-gray-400">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleColors[u.role]}`}>
                        {u.role.replace("_"," ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{u.department || "-"}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {u.manager?.name && <div className="text-xs"><span className="text-gray-400">Mgr:</span> {u.manager.name}</div>}
                      {u.departmentHead?.name && <div className="text-xs"><span className="text-gray-400">DH:</span> {u.departmentHead.name}</div>}
                      {!u.manager?.name && !u.departmentHead?.name && "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock size={12} />
                        <span>{u._count?.workLogs || 0} logs</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.isDeleted ? (
                        <span className="text-xs px-2 py-1 rounded-full font-medium bg-red-100 text-red-700">Deleted</span>
                      ) : (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${u.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {u.isActive ? "Active" : "Disabled"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => navigate(`/users/${u.id}`)} title="View Activity"
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Eye size={15} />
                        </button>
                        {isAdmin && !u.isDeleted && (
                          <>
                            <button onClick={() => openEdit(u)} title="Edit & Tab Access"
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                              <Pencil size={15} />
                            </button>
                            <button onClick={() => toggleActive(u.id, u.name)} title={u.isActive ? "Disable" : "Enable"}
                              className={`p-1.5 rounded-lg transition-colors ${u.isActive ? "text-gray-400 hover:text-orange-600 hover:bg-orange-50" : "text-gray-400 hover:text-green-600 hover:bg-green-50"}`}>
                              {u.isActive ? <UserX size={15} /> : <UserCheck size={15} />}
                            </button>
                            <button onClick={() => deleteUser(u.id, u.name)} title="Delete"
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                        {isAdmin && u.isDeleted && (
                          <button onClick={() => restoreUser(u.id, u.name)} title="Restore User"
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1">
                            <RotateCcw size={15} />
                            <span className="text-xs">Restore</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="text-center py-12 text-gray-400">No users found</div>}
          </div>
        </>
      )}

      {activeTab === "requests" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Name","Email","Department","Role","Status","Date","Actions"].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{r.name}</td>
                  <td className="px-4 py-3 text-gray-500">{r.email}</td>
                  <td className="px-4 py-3 text-gray-500">{r.department || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleColors[r.role] || "bg-gray-100 text-gray-700"}`}>
                      {r.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      r.status==="APPROVED" ? "bg-green-100 text-green-700" :
                      r.status==="REJECTED" ? "bg-red-100 text-red-700" :
                      "bg-yellow-100 text-yellow-700"}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{new Date(r.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {r.status === "PENDING" && isAdmin && (
                      <div className="flex gap-2">
                        <button onClick={() => approveRequest(r.id)}
                          className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs hover:bg-green-700">
                          <UserCheck size={12} /> Approve
                        </button>
                        <button onClick={() => rejectRequest(r.id)}
                          className="flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs hover:bg-red-200">
                          <UserX size={12} /> Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">No account requests</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold">{editUser ? "Edit User" : "Create User"}</h3>
              <button onClick={() => setShowForm(false)}><X size={22} className="text-gray-400" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              {!editUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required
                    placeholder="Temporary password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                    {ROLES.map(r => <option key={r} value={r}>{r.replace("_"," ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select value={form.department} onChange={e => setForm({...form, department: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="">Select...</option>
                    {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign Manager</label>
                <select value={form.managerId} onChange={e => setForm({...form, managerId: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                  <option value="">No Manager</option>
                  {managers.filter(m => m.id !== editUser?.id).map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role.replace("_"," ")})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign Department Head</label>
                <select value={form.departmentHeadId} onChange={e => setForm({...form, departmentHeadId: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                  <option value="">No Department Head</option>
                  {deptHeads.filter(d => d.id !== editUser?.id).map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              {editUser && (
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="isActive" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} className="w-4 h-4" />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Account Active</label>
                </div>
              )}

              {/* Tab Access */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Shield size={16} className="text-blue-600" /> Tab Access
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setTabPerms(ALL_TABS.map(t=>t.key))}
                      className="text-xs text-blue-600 hover:underline">All</button>
                    <span className="text-gray-300">|</span>
                    <button type="button" onClick={() => setTabPerms([])}
                      className="text-xs text-red-500 hover:underline">None</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_TABS.map(tab => (
                    <label key={tab.key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg px-2 py-1.5">
                      <input type="checkbox" checked={tabPerms.includes(tab.key)} onChange={() => toggleTab(tab.key)}
                        className="w-4 h-4 text-blue-600 rounded" />
                      <span className="text-sm text-gray-700">{tab.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm hover:bg-blue-700">{editUser ? "Update User" : "Create User"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default Users;
